import {
  type Control,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { FormTooltip } from "@/components/ui/form-description";

export interface TextFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends React.HTMLAttributes<HTMLDivElement> {
  control: Control<TFieldValues>;
  description?: string;
  descriptionAsTooltip?: boolean;
  label: string;
  name: TName;
  placeholder?: string;
  required?: boolean;
  requiredLabel?: string;
  visuallyHiddenLabel?: boolean;
}

export function TextField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  description,
  descriptionAsTooltip = false,
  label,
  name,
  placeholder,
  required,
  requiredLabel = "Required",
  visuallyHiddenLabel = false,
  className,
  ...htmlProps
}: Readonly<TextFieldProps<TFieldValues, TName>>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className} {...htmlProps}>
          <div className={cn(visuallyHiddenLabel ? "sr-only" : "")}>
            <FormLabel className="inline-block leading-snug">
              {label}
              {required ? (
                <>
                  <span className="ms-1 text-red-500" aria-hidden="true">
                    *
                  </span>
                  <span className="sr-only">({requiredLabel})</span>
                </>
              ) : null}
            </FormLabel>
            {description && !descriptionAsTooltip ? (
              <FormDescription>{description}</FormDescription>
            ) : null}
            {description && descriptionAsTooltip ? (
              <FormTooltip description={description} />
            ) : null}
          </div>
          <FormControl>
            <Input
              type="text"
              aria-required={required ? "true" : "false"}
              placeholder={placeholder}
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
